import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext.jsx';
import { useTenant } from '../components/TenantContext.jsx';
import { statsApi } from '../api/stats.js';
import { planApi } from '../api/tenants.js';
import { PrepaidBilling } from '../components/MinuteQuota.jsx';
import { accountsApi } from '../api/accounts.js';
import { MonthPager, currentMonthTaipei } from '../components/SuperAdmin/MonthPager.jsx';
import { PageHeader, SectionHeader } from '../components/ui/PageHeader.jsx';
import { Card, Empty } from '../components/ui/Card.jsx';
import {
    BarChart3, TrendingUp, PhoneIncoming, PhoneOutgoing, RefreshCw, UserSquare2, ChevronDown,
    PhoneOff, PhoneForwarded, CheckCircle2, CircleAlert, Receipt, Star, Search, Globe, MessageSquare, Download,
} from 'lucide-react';
import { overlayDismiss } from '../lib/overlayDismiss';

/* Plan fallback — /me/plan API 還沒回前的 placeholder（不顯示給用戶看到） */
const FALLBACK_PLAN = {
    id: 'plan_01',
    name: 'AI 純轉接',
    monthlyQuotaMinutes: 400,
    overageRateUsdPerMin: 0.10,
};

const RANGES = [
    // 📌 0915：eyebrowLabel 拿掉寫死的英文（'last 7 days'）—— 使用者看不出「7 日」到底哪天到哪天
    //    （實際是今天 00:00 往回推 6 天，今天只算到現在）。改由 rangeMillis() 統一組實際日期，
    //    preset 與自訂同一種寫法。
    { key: '1d', labelKey: 'range.today', days: 1 },
    { key: '7d', labelKey: 'range.7d', days: 7 },
    { key: '30d', labelKey: 'range.30d', days: 30 },
    // 自訂起訖。刻意**不帶 days** —— 它的區間由使用者選的兩個日期決定，
    // 也刻意**不把日期塞進 rangeKey 字串**（`custom:from-to` 那種要 parse 的形式），
    // 日期另外用 state 存，rangeKey 維持單純的列舉值。
    { key: 'custom', labelKey: 'range.custom', days: null },
];

/** 自訂區間上限。逐日趨勢是按天查的，選一整年會把查詢量放大。0915：92 → 31（jacob 裁示）。 */
const MAX_CUSTOM_DAYS = 31;

const VIEWS = [
    { key: 'overview', labelKey: 'view.overview' },
    { key: 'avatar', labelKey: 'view.avatar' },
];

const TZ_OFFSET = 8 * 3600 * 1000;

/** `YYYY-MM-DD` → 該日 UTC+8 的 00:00（毫秒）。非法字串回 null。 */
function utc8DayStart(ymd) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || '').trim());
    if (!m) return null;
    const [, y, mo, d] = m;
    const t = Date.UTC(Number(y), Number(mo) - 1, Number(d), 0, 0, 0) - TZ_OFFSET;
    return Number.isFinite(t) ? t : null;
}

/** 毫秒 → UTC+8 當天的 `YYYY-MM-DD`（date input 的 value 格式，與 utc8DayStart 互為反函式） */
function utc8DateStr(ms) {
    const d = new Date(ms + TZ_OFFSET);
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

/** 把 UTC+8 的日界格式化成 `8/15` */
function utc8MonthDay(ms) {
    const d = new Date(ms + TZ_OFFSET);
    return `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
}

/**
 * rangeKey（＋自訂起訖）→ 查詢區間。
 *
 * ⚠️ **三個 preset 的算法一個字都沒動**（`1d`/`7d`/`30d` 走的還是原本那段），
 *    改動只是在前面多一條 `custom` 的分支。這頁最容易被改壞的就是 UTC+8 對齊差一天。
 *
 * ⚠️ 自訂區間**沿用本頁的 UTC+8 日界**，不是 CallsPage 的 `setHours(0,0,0,0)`（瀏覽器本地時區）。
 *    CallsPage 的**互動行為**（兩個 date input、沒填齊就不套用）照抄，但日界必須跟本頁的
 *    preset 與計費卡片同一套，否則在非 UTC+8 的機器上「自訂 30 天」跟「近 30 日」會差一天。
 *
 * @returns preset → { fromMillis, toMillis, days, eyebrowLabel }
 *          自訂未填齊 → { pending: true }（呼叫端不要送查詢）
 *          自訂不合法 → { invalid: 'reversed' | 'too_long', days? }
 */
function rangeMillis(rangeKey, customFrom, customTo) {
    const now = Date.now();
    if (rangeKey === 'custom') {
        const from = utc8DayStart(customFrom);
        const startOfEndDay = utc8DayStart(customTo);
        if (from == null || startOfEndDay == null) return { pending: true };
        if (startOfEndDay < from) return { invalid: 'reversed' };
        // 訖日算到當天 23:59:59.999；但不超過「現在」—— 與 preset 的 `toMillis: now` 同語意，
        // 選到未來日期也不會去查一段還沒發生的時間。
        const endOfDay = startOfEndDay + 86400000 - 1;
        const toMillis = Math.min(endOfDay, now);
        const days = Math.round((startOfEndDay - from) / 86400000) + 1;   // 含頭含尾
        if (days > MAX_CUSTOM_DAYS) return { invalid: 'too_long', days };
        return {
            fromMillis: from,
            toMillis,
            days,
            eyebrowLabel: days === 1
                ? utc8MonthDay(from)
                : `${utc8MonthDay(from)} – ${utc8MonthDay(startOfEndDay)}`,
            fromDate: customFrom,
            toDate: customTo,
            custom: true,
        };
    }
    const r = RANGES.find((x) => x.key === rangeKey && x.days) || RANGES[1];
    const tzOffset = TZ_OFFSET;
    const todayStartUtc8 = Math.floor((now + tzOffset) / 86400000) * 86400000 - tzOffset;
    const fromMillis = todayStartUtc8 - (r.days - 1) * 86400000;
    return {
        fromMillis,
        toMillis: now,
        days: r.days,
        // 實際日期；1 天不寫成「09/15 – 09/15」
        eyebrowLabel: r.days === 1
            ? utc8MonthDay(fromMillis)
            : `${utc8MonthDay(fromMillis)} – ${utc8MonthDay(todayStartUtc8)}`,
        // 日期框常駐後，preset 的起訖要回填進兩個 input（YYYY-MM-DD）
        fromDate: utc8DateStr(fromMillis),
        toDate: utc8DateStr(todayStartUtc8),
    };
}

/* 本月 UTC+8 起算（從 1 號 00:00 到現在） */
function currentMonthRange() {
    const tzOffset = 8 * 3600 * 1000;
    const now = new Date(Date.now() + tzOffset);
    const y = now.getUTCFullYear();
    const m = now.getUTCMonth();
    const fromUtc8 = Date.UTC(y, m, 1, 0, 0, 0) - tzOffset;
    const nextMonthUtc8 = Date.UTC(y, m + 1, 1, 0, 0, 0) - tzOffset;
    const daysInMonth = Math.round((nextMonthUtc8 - fromUtc8) / 86400000);
    const dayOfMonth = now.getUTCDate();
    const remainingDays = Math.max(0, daysInMonth - dayOfMonth);
    return {
        fromMillis: fromUtc8,
        toMillis: Date.now(),
        // `2026-09` 已足以表達「本月」，右側還有 DAY 15/30 · REMAINING 15D；
        // 區間對照改由下方 TOTAL AIRTIME 自己標（那才是會跟著按鈕變的那半）。
        label: `${y}-${String(m + 1).padStart(2, '0')}`,
        dayOfMonth,
        daysInMonth,
        remainingDays,
    };
}

function formatDuration(sec) {
    sec = Math.max(0, Math.round(Number(sec) || 0));
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    return { h, m, s };
}

function shortDuration(sec) {
    const { h, m, s } = formatDuration(sec);
    if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
    if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
    return `${s}s`;
}

/**
 * 一律顯示到秒 —— 給「分項必須加得回總數」的地方用（計費卡、AIRTIME 組成）。
 *
 * 🔴 0915 實案：shortDuration 在 h>0 時直接丟秒，電話 6057 秒（1H40M57S）顯示成「1H 40M」，
 *    使用者照畫面手算 100 + 15.6×0.5 = 107.8，跟大數字 109 對不起來 —— 被吃掉的 57 秒就是差額。
 *    計費金額本身是對的（usedSec 全程以秒累加），錯的是顯示精度。
 */
function preciseDuration(sec) {
    const { h, m, s } = formatDuration(sec);
    if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
    if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
    return `${s}s`;
}

export default function DashboardPage() {
    const { t } = useTranslation('dashboard');
    const { hostId, isAdmin, accountRole } = useTenant();
    const { user } = useAuth();
    const [rangeKey, setRangeKey] = useState('7d');
    // 自訂起訖（`YYYY-MM-DD`）。刻意跟 rangeKey 分開存，不塞進 key 字串再 parse。
    const [customFrom, setCustomFrom] = useState('');
    const [customTo, setCustomTo] = useState('');
    // 📌 輸入框的值（customFrom/To）與「真正送出查詢的值」（appliedFrom/To）分開：
    //    使用者改日期時輸入框先變，debounce 或按放大鏡後才寫進 applied → 不會邊打字邊打 API。
    const [appliedFrom, setAppliedFrom] = useState('');
    const [appliedTo, setAppliedTo] = useState('');
    const [view, setView] = useState('overview');

    // 子帳號只看個別客服、沒有整體：強制角色檢視
    useEffect(() => {
        if (!isAdmin) setView('avatar');
    }, [isAdmin]);
    const [selectedAvatarId, setSelectedAvatarId] = useState('');
    const [overview, setOverview] = useState(null);
    const [avatarData, setAvatarData] = useState(null);
    const [billingMonth, setBillingMonth] = useState(null);
    const [userPlan, setUserPlan] = useState(null);
    const [userSeats, setUserSeats] = useState(null);   // { plan_01, plan_02 }；null = 未設（不限）
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [tick, setTick] = useState(0);

    const range = useMemo(() => rangeMillis(rangeKey, appliedFrom, appliedTo), [rangeKey, appliedFrom, appliedTo]);
    // 輸入框當下的值合不合法（給提示用；查詢一律看 range）
    const draft = useMemo(() => rangeMillis('custom', customFrom, customTo), [customFrom, customTo]);

    // preset → 回填日期框（顯示實際查了哪段）。自訂時不覆蓋使用者打的值。
    useEffect(() => {
        if (rangeKey === 'custom') return;
        const r = rangeMillis(rangeKey, '', '');
        setCustomFrom(r.fromDate);
        setCustomTo(r.toDate);
        setAppliedFrom(r.fromDate);
        setAppliedTo(r.toDate);
    }, [rangeKey]);

    // 改日期 → 切自訂高亮 + debounce 600ms 自動查（合法才查）
    useEffect(() => {
        if (!customFrom || !customTo) return;
        const r = rangeMillis('custom', customFrom, customTo);
        if (r.pending || r.invalid) return;                       // 顛倒 / 超過上限 → 不查，只顯示提示
        if (customFrom === appliedFrom && customTo === appliedTo) return;   // 沒變就不查（含 preset 回填那次）
        const timer = setTimeout(() => {
            setRangeKey('custom');
            setAppliedFrom(customFrom);
            setAppliedTo(customTo);
        }, 600);
        return () => clearTimeout(timer);
    }, [customFrom, customTo, appliedFrom, appliedTo]);

    /** 放大鏡：跳過 debounce 立刻套用 */
    const applyDates = () => {
        const r = rangeMillis('custom', customFrom, customTo);
        if (r.pending || r.invalid) return;
        setRangeKey('custom');
        setAppliedFrom(customFrom);
        setAppliedTo(customTo);
        setTick((n) => n + 1);
    };
    // 自訂未填齊 / 不合法 → 一律不送查詢（沿用 CallsPage 的「沒填齊就先不套用」行為）
    const rangeReady = range.fromMillis != null && range.toMillis != null;
    // 計費卡片的區間：選了自訂且合法 → 跟著自訂區間；否則維持原本的「本月」。
    // ⚠️ 只換時間範圍，**計費口徑一個字都沒動**（web×webMinuteFactor、ivr_test/web_text 不計、
    //    billableSec 扣掉轉真人 ring）—— 那是 BillingPanel 與後端的事，這裡只給 from/to。
    const monthRange = useMemo(() => {
        if (rangeKey === 'custom' && rangeReady) {
            return {
                fromMillis: range.fromMillis,
                toMillis: range.toMillis,
                label: range.eyebrowLabel,
                custom: true,
                days: range.days,
            };
        }
        return currentMonthRange();
    }, [rangeKey, rangeReady, range.fromMillis, range.toMillis, range.eyebrowLabel, range.days, tick]);

    const billingConfig = useMemo(() => {
        const p = userPlan || FALLBACK_PLAN;
        // 🔴 monthlyQuotaMinutes 是「每席」的含量（plans.js：800 分鐘 / 坐席 / 月），
        //    實際含量 = 每席含量 × 席次。開帳單那側早就這樣算
        //    （saasAdmin: includedMinutes = 800 * csSeats），dashboard 之前漏了乘席次，
        //    買 5 席的客戶看到 1,638 / 800 被標成「超額 $83.80」，實際含量 4,000 沒超。
        //    席次沒設（經銷商 / 超管不限）→ 當 1 席，維持原本的顯示。
        const seatCount = Math.max(1, Number(userSeats?.[p.id]) || 1);
        return {
            planId: p.id,
            planName: p.name,
            quotaMinutesPerSeat: p.monthlyQuotaMinutes,
            seatCount,
            monthlyQuotaMinutes: p.monthlyQuotaMinutes == null ? null : p.monthlyQuotaMinutes * seatCount,
            overageRateUsdPerMin: p.overageRateUsdPerMin,
            webMinuteFactor: p.webMinuteFactor ?? 0.5,   // 網頁通話折算係數（後端 plans.js WEB_MINUTE_FACTOR）
            loaded: !!userPlan,
        };
    }, [userPlan, userSeats]);

    // user plan — quota / overage rate 跟著 user 的 plan 走
    useEffect(() => {
        let cancelled = false;
        planApi.me()
            .then((r) => { if (!cancelled) { setUserPlan(r.plan); setUserSeats(r.seats || null); } })
            .catch(() => { if (!cancelled) setUserPlan(null); });
        return () => { cancelled = true; };
    }, [tick]);

    // 加值分鐘數（預付帳號才有內容；後付回 { billingMode:'postpaid' } → 卡片不顯示、計費卡片照舊）
    const [minuteBalance, setMinuteBalance] = useState(null);
    useEffect(() => {
        let cancelled = false;
        planApi.minuteBalance(hostId)
            .then((r) => { if (!cancelled) setMinuteBalance(r.balance); })
            .catch(() => { if (!cancelled) setMinuteBalance(null); });
        return () => { cancelled = true; };
    }, [tick, hostId]);

    // 真人客服服務滿意度（webcall handoff 結束後客戶評分）— 跟著 rangeKey
    const [ratings, setRatings] = useState(null);
    useEffect(() => {
        if (!hostId || !rangeReady) return;
        let cancelled = false;
        statsApi
            .handoffRatings(hostId, { fromMillis: range.fromMillis, toMillis: range.toMillis })
            .then((r) => { if (!cancelled) setRatings(r); })
            .catch(() => { if (!cancelled) setRatings(null); });
        return () => { cancelled = true; };
    }, [hostId, rangeReady, range.fromMillis, range.toMillis, tick]);

    // 計費 — 預設用本月；選了自訂區間時改用該區間（見 monthRange）
    useEffect(() => {
        if (!hostId || monthRange.fromMillis == null) return;
        let cancelled = false;
        statsApi
            .callStats(hostId, { fromMillis: monthRange.fromMillis, toMillis: monthRange.toMillis })
            .then((r) => { if (!cancelled) setBillingMonth(r); })
            .catch(() => { if (!cancelled) setBillingMonth(null); });
        return () => { cancelled = true; };
    }, [hostId, monthRange.fromMillis, monthRange.toMillis, tick]);

    // overview always fetched — its byAvatar feeds the avatar dropdown
    useEffect(() => {
        if (!hostId || !rangeReady) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        statsApi
            .callStats(hostId, { fromMillis: range.fromMillis, toMillis: range.toMillis })
            .then((r) => {
                if (cancelled) return;
                setOverview(r);
                // auto-select first avatar if none chosen
                if (!selectedAvatarId && r.byAvatar?.length > 0) {
                    setSelectedAvatarId(r.byAvatar[0].avatarId);
                }
            })
            .catch((e) => {
                if (cancelled) return;
                setError(e);
            })
            .finally(() => {
                if (cancelled) return;
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hostId, rangeReady, range.fromMillis, range.toMillis, tick]);

    // avatar-specific fetch
    useEffect(() => {
        if (view !== 'avatar' || !hostId || !selectedAvatarId || !rangeReady) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        statsApi
            .callStats(hostId, {
                fromMillis: range.fromMillis,
                toMillis: range.toMillis,
                avatarId: selectedAvatarId,
            })
            .then((r) => {
                if (cancelled) return;
                setAvatarData(r);
            })
            .catch((e) => {
                if (cancelled) return;
                setError(e);
            })
            .finally(() => {
                if (cancelled) return;
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [view, hostId, selectedAvatarId, rangeReady, range.fromMillis, range.toMillis, tick]);

    if (!hostId) {
        return (
            <div>
                <PageHeader title={t('header.title')} />
                <Empty icon={BarChart3} title={t('empty.noTenant.title')} description={t('empty.noTenant.desc')} />
            </div>
        );
    }

    const data = view === 'avatar' ? avatarData : overview;
    const selectedAvatar = overview?.byAvatar?.find((a) => a.avatarId === selectedAvatarId);

    return (
        <div className="dashboard-stage">
            <PageHeader
                title={t('header.title')}
                description={
                    view === 'avatar'
                        ? t('header.descAvatar')
                        : t('header.descOverview')
                }
                actions={
                    /* items-start：自訂展開後這一區會變高，重新整理鈕要對齊 pill 那一列 */
                    <div className="flex items-start gap-2">
                        <RangeSwitcher
                            rangeKey={rangeKey} onChange={setRangeKey}
                            customFrom={customFrom} setCustomFrom={setCustomFrom}
                            customTo={customTo} setCustomTo={setCustomTo}
                            invalid={draft.invalid} days={draft.days}
                            onSearch={applyDates}
                            busy={loading}
                        />
                        <MonthlyReportButton
                            uid={user?.uid}
                            enabled={accountRole === 'business'}
                            busy={loading}
                        />
                        <button
                            onClick={() => setTick((n) => n + 1)}
                            title={t('common:actions.refresh')}
                            disabled={loading}
                            className="size-8 rounded-full border border-cream-200 bg-white hover:bg-cream-100 transition flex items-center justify-center text-ink-500 disabled:opacity-40"
                        >
                            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
                        </button>
                    </div>
                }
            />

            <ViewBar
                view={view}
                onChangeView={setView}
                avatars={overview?.byAvatar || []}
                selectedAvatarId={selectedAvatarId}
                onChangeAvatar={setSelectedAvatarId}
                isAdmin={isAdmin}
                busy={loading}
            />

            {error && (
                <div className="mb-6 px-4 py-3 rounded-md border border-danger-500/30 bg-danger-100/40 text-sm text-danger-500">
                    {t('error.loadFailed', { message: error.message })}
                </div>
            )}

            {data?.truncated && (
                <div className="mb-6 px-4 py-2.5 rounded-md border border-warn-500/30 bg-warn-100/60 text-xs text-ink-700">
                    <span className="font-mono uppercase tracking-[0.18em] text-warn-500 mr-2">notice</span>
                    {t('notice.truncated', { count: data.sampledCount.toLocaleString() })}
                </div>
            )}

            {isAdmin && (
                <div className="reveal" style={{ animationDelay: '60ms' }}>
                    {/* 預付帳號：本月計費換成「免費 + 加購」版（額度、兩條用量條、最近到期）；後付照舊 */}
                    {minuteBalance?.billingMode === 'prepaid' ? (
                        <PrepaidBilling balance={minuteBalance} planName={billingConfig.planName} planId={billingConfig.planId} />
                    ) : (
                        <BillingPanel
                            monthData={billingMonth}
                            monthRange={monthRange}
                            config={billingConfig}
                        />
                    )}
                </div>
            )}

            {view === 'avatar' && overview?.byAvatar?.length === 0 ? (
                <Card padded={false}>
                    <Empty
                        icon={UserSquare2}
                        title={t('empty.noAvatarData.title')}
                        description={t('empty.noAvatarData.desc')}
                    />
                </Card>
            ) : (
                <>
                    {/* 📌 0915：選完日期後下方 UI 全會變 —— 標頭宣告「以下都依這個區間」。
                        寫法照抄計費卡的 `● BILLING · 2026-09` 眉標，整頁語彙才一致
                        （第一版自創了一條橫貫全寬的細線，跟頁面其他區塊對不起來）。 */}
                    <div className="mb-5 pb-4 border-b border-cream-200 reveal" style={{ animationDelay: '40ms' }}>
                        <div className="inline-flex items-center gap-2">
                            <span className="size-1.5 rounded-full bg-accent-500" />
                            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">
                                {t('section.callStats')} · {range.eyebrowLabel}
                            </span>
                        </div>
                        <p className="text-sm text-ink-500 mt-2 leading-relaxed">
                            {t('section.callStats.desc')}
                        </p>
                    </div>

                    <KpiStrip loading={loading} data={data} rangeLabel={range.eyebrowLabel} />

                    <div className="reveal mt-12" style={{ animationDelay: '320ms' }}>
                        <SectionHeader title={t('section.daily.title')} description={t('section.daily.desc')} />
                        <Card>
                            <DailyTrendChart byDay={data?.byDay} fromMillis={range.fromMillis} toMillis={range.toMillis} />
                        </Card>
                    </div>

                    <div className="reveal mt-10" style={{ animationDelay: '380ms' }}>
                        <SectionHeader title={t('section.termination.title')} description={t('section.termination.desc')} />
                        <TerminationBreakdown byTermination={data?.byTermination} totalCalls={data?.totals?.calls} />
                    </div>

                    {view === 'overview' && (
                        <div className="reveal mt-10" style={{ animationDelay: '410ms' }}>
                            <SectionHeader
                                title={t('section.satisfaction.title')}
                                description={t('section.satisfaction.desc')}
                            />
                            <SatisfactionPanel data={ratings} />
                        </div>
                    )}

                    {view === 'overview' && (
                        <div className="reveal mt-10" style={{ animationDelay: '440ms' }}>
                            <SectionHeader
                                title={t('section.ranking.title')}
                                description={t('section.ranking.desc')}
                            />
                            <AvatarRanking
                                rows={overview?.byAvatar}
                                totalDurationSec={overview?.totals?.durationSec}
                                loading={loading}
                                onPick={(id) => {
                                    setSelectedAvatarId(id);
                                    setView('avatar');
                                }}
                            />
                        </div>
                    )}
                </>
            )}

            <StageStyles />
        </div>
    );
}

/* ───────────────────────── 服務滿意度（真人客服 handoff 評分） ───────────────────────── */
function Stars({ value, size = 15 }) {
    return (
        <span className="inline-flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((n) => (
                <Star
                    key={n}
                    size={size}
                    strokeWidth={1.5}
                    className={n <= Math.round(value || 0) ? 'fill-amber-400 text-amber-400' : 'text-cream-300'}
                />
            ))}
        </span>
    );
}

function SatisfactionPanel({ data }) {
    const { t } = useTranslation('dashboard');
    const count = data?.count || 0;
    if (!count) {
        return (
            <Card>
                <div className="flex items-center gap-3 py-2 text-sm text-ink-400">
                    <Star size={16} className="text-cream-300" />
                    {t('satisfaction.empty')}
                </div>
            </Card>
        );
    }
    const dist = data.dist || {};
    const max = Math.max(1, ...Object.values(dist).map(Number));
    return (
        <Card>
            <div className="flex flex-wrap items-start gap-x-10 gap-y-6">
                {/* 平均分 */}
                <div className="min-w-[140px]">
                    <div className="flex items-baseline gap-2">
                        <span className="text-4xl font-semibold tracking-tight text-ink-900 tabular-nums">{(data.avg ?? 0).toFixed(1)}</span>
                        <span className="text-sm text-ink-400">/ 5</span>
                    </div>
                    <div className="mt-1.5"><Stars value={data.avg} size={17} /></div>
                    <div className="mt-1.5 text-xs text-ink-400">{t('satisfaction.count', { n: count })}</div>
                </div>
                {/* 分佈 5→1 */}
                <div className="flex-1 min-w-[220px] max-w-md">
                    {[5, 4, 3, 2, 1].map((n) => (
                        <div key={n} className="mb-1.5 flex items-center gap-2 text-xs">
                            <span className="w-3 text-right font-mono text-ink-500 tabular-nums">{n}</span>
                            <Star size={11} className="fill-amber-400 text-amber-400 flex-shrink-0" />
                            <div className="h-2 flex-1 overflow-hidden rounded-full bg-cream-200">
                                <div className="h-full rounded-full bg-amber-400" style={{ width: `${((dist[n] || 0) / max) * 100}%` }} />
                            </div>
                            <span className="w-6 text-right font-mono text-ink-400 tabular-nums">{dist[n] || 0}</span>
                        </div>
                    ))}
                </div>
                {/* 依專員 */}
                {data.byAgent?.length > 0 && (
                    <div className="min-w-[220px] flex-1">
                        <div className="mb-2 grid grid-cols-[1fr_auto_auto] gap-x-4 text-[10px] font-mono uppercase tracking-[0.15em] text-ink-400">
                            <span>{t('satisfaction.col.agent')}</span>
                            <span className="text-right">{t('satisfaction.col.ratings')}</span>
                            <span className="text-right">{t('satisfaction.col.avg')}</span>
                        </div>
                        {data.byAgent.slice(0, 6).map((a) => (
                            <div key={a.uid || a.name} className="grid grid-cols-[1fr_auto_auto] items-center gap-x-4 border-t border-cream-200 py-1.5 text-sm">
                                <span className="truncate text-ink-700">{a.name || '—'}</span>
                                <span className="text-right font-mono text-xs text-ink-400 tabular-nums">{a.count}</span>
                                <span className="flex items-center justify-end gap-1.5 font-mono text-xs text-ink-700 tabular-nums">
                                    {a.avg.toFixed(1)} <Star size={11} className="fill-amber-400 text-amber-400" />
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Card>
    );
}

/* ───────────────────────── View bar (tabs + avatar picker) ───────────────────────── */
function ViewBar({ view, onChangeView, avatars, selectedAvatarId, onChangeAvatar, isAdmin = true, busy = false }) {
    const { t } = useTranslation('dashboard');
    return (
        <div className="mb-8 flex items-center gap-4 flex-wrap" aria-busy={busy}>
            {/* 子帳號只有「角色檢視」，不顯示總覽切換 */}
            {isAdmin && (
                <div className="inline-flex items-center rounded-full border border-cream-200 bg-white p-0.5">
                    {VIEWS.map((v) => {
                        const active = v.key === view;
                        return (
                            <button
                                key={v.key}
                                onClick={() => onChangeView(v.key)}
                                disabled={busy}
                                className={[
                                    'px-4 py-1.5 rounded-full text-[12px] font-medium tracking-tight transition',
                                    'disabled:cursor-not-allowed',
                                    active
                                        ? 'bg-ink-900 text-cream-50'
                                        : 'text-ink-500 hover:text-ink-900 disabled:hover:text-ink-500',
                                    busy && !active ? 'opacity-45' : '',
                                ].join(' ')}
                            >
                                {t(v.labelKey)}
                            </button>
                        );
                    })}
                </div>
            )}
            {view === 'avatar' && (
                <AvatarPicker
                    avatars={avatars}
                    selectedAvatarId={selectedAvatarId}
                    onChange={onChangeAvatar}
                />
            )}
        </div>
    );
}

function AvatarPicker({ avatars, selectedAvatarId, onChange }) {
    const { t } = useTranslation('dashboard');
    const [open, setOpen] = useState(false);
    const selected = avatars.find((a) => a.avatarId === selectedAvatarId);
    if (avatars.length === 0) {
        return (
            <div className="text-xs text-ink-400 font-mono px-3 py-1.5 rounded border border-cream-200 bg-cream-100">
                no avatars in range
            </div>
        );
    }
    return (
        <div className="relative">
            <button
                onClick={() => setOpen((o) => !o)}
                className="flex items-center gap-2.5 px-4 py-2 rounded-full border border-cream-200 bg-white hover:bg-cream-100 transition"
            >
                <UserSquare2 size={14} className="text-accent-500" strokeWidth={1.8} />
                <span className="text-sm font-medium text-ink-900 max-w-[200px] truncate">
                    {selected?.avatarName || t('picker.selectAvatar')}
                </span>
                <span className="font-mono text-[10px] text-ink-400 tnum tracking-tight">
                    {t('unit.callsCount', { count: selected?.calls ?? 0 })}
                </span>
                <ChevronDown size={14} className="text-ink-400" />
            </button>
            {open && (
                <>
                    <div className="fixed inset-0 z-20" {...overlayDismiss(() => setOpen(false))} />
                    <ul className="absolute top-full left-0 mt-2 w-80 bg-white border border-cream-200 rounded-md shadow-lg z-30 py-1 max-h-96 overflow-y-auto">
                        {avatars.map((a) => (
                            <li key={a.avatarId}>
                                <button
                                    onClick={() => {
                                        onChange(a.avatarId);
                                        setOpen(false);
                                    }}
                                    className={[
                                        'w-full text-left px-3 py-2.5 hover:bg-cream-100 transition flex items-center justify-between gap-3',
                                        a.avatarId === selectedAvatarId ? 'bg-cream-50' : '',
                                    ].join(' ')}
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-medium text-ink-900 truncate">{a.avatarName}</div>
                                        <div className="font-mono text-[10px] text-ink-300 mt-0.5 truncate">{a.avatarId}</div>
                                    </div>
                                    <div className="text-right shrink-0 tnum">
                                        <div className="text-xs text-ink-700 font-medium">{t('unit.callsCount', { count: a.calls })}</div>
                                        <div className="text-[10px] text-ink-400 mt-0.5">{shortDuration(a.durationSec)}</div>
                                    </div>
                                </button>
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </div>
    );
}

/* ───────────────────────── Range switcher ───────────────────────── */
/**
 * 時間區間切換器：pill 群組 ＋（選「自訂」時）下方展開的起訖日期列。
 *
 * 展開／收合用 `grid-template-rows: 0fr → 1fr` ＋ `overflow-hidden`：
 *   - 元素**保持掛載**，只是高度收成 0 → **退場動畫播得出來**（直接 unmount 是看不到收合過程的）
 *   - 不用測量高度、內容變高也不會失準
 *
 * ⚠️ 保持掛載的代價是「看不見但還在」，所以收合時一定要：
 *   - `inert` —— 否則按 Tab 會跳進一個看不見的輸入框（React 19 原生支援這個屬性）
 *   - `aria-hidden` + `tabIndex={-1}` 當作舊瀏覽器的後備
 * ⚠️ `motion-reduce:transition-none` —— 使用者設了「減少動態」就直接顯示/隱藏，不做過渡。
 *
 * 📌 0915 改版（jacob）：
 *   - 日期框**常駐**，不再是點「自訂」才展開 —— 使用者一眼看得到查的是哪段，也能直接點日期微調。
 *   - 按 preset（今日/7 日/30 日）會把該區間的起訖**回填**進兩個框；使用者一改日期就自動切到「自訂」
 *     高亮（範圍已經不是 preset 了，繼續讓 preset 亮著會誤導）。
 *   - 改完日期 debounce 600ms 且區間合法才查；放大鏡保留給「想立刻看」的情況。
 *     不即時查的原因：改起日時迄日還是舊的，中間會閃「起訖顛倒」或「超過上限」，而且會多打 API。
 */
/**
 * busy：查詢進行中 → 整組控制項鎖住（用原生 disabled，不是只做視覺）。
 * 連點「7 日 → 30 日 → 自訂」會疊送三次查詢，回來的順序不保證，畫面可能停在中間那次的結果。
 */
/**
 * 月報表下載鈕（商用帳號自行下載）。
 *
 * 報表是按月結算的，跟頁面上方那個「今日／7 日／30 日／自訂」的區間無關 ——
 * 所以自己帶一個月份選擇器，不吃 rangeKey。預設當月，可往前翻。
 *
 * 只有商用帳號看得到：經銷商與超管的報表入口在「帳號管理 → 商用帳號」那頁（每列一顆），
 * 而席次/免費額度是帳號層的概念，經銷商自己沒有這組數字。
 */
function MonthlyReportButton({ uid, enabled, busy }) {
    const { t, i18n } = useTranslation('dashboard');
    const [open, setOpen] = useState(false);
    const [month, setMonth] = useState(() => currentMonthTaipei());
    const [downloading, setDownloading] = useState(false);
    if (!enabled || !uid) return null;

    async function go() {
        setDownloading(true);
        try {
            await accountsApi.downloadReport(uid, month, { lang: i18n.language });
            setOpen(false);
        } catch (e) {
            alert(e.message);
        } finally {
            setDownloading(false);
        }
    }

    return (
        <div className="relative">
            <button
                onClick={() => setOpen((v) => !v)}
                disabled={busy}
                title={t('report.buttonTitle')}
                aria-expanded={open}
                className="h-8 px-3 rounded-full border border-cream-200 bg-white hover:bg-cream-100 transition flex items-center gap-1.5 text-[12px] text-ink-700 disabled:opacity-40"
            >
                <Download size={13} /> {t('report.button')}
            </button>
            {open && (
                <>
                    {/* 點外面關閉 —— 走 overlayDismiss 而不是 onClick：在面板裡按住拖曳、放開時
                        滑到遮罩上，click 的 target 會變成遮罩 → 面板被誤關（那個 helper 就是為了
                        這個踩過坑才做的）。同檔的 AvatarPicker 也是這樣接。
                        不用 portal，這顆鈕沒有 overflow 祖先。 */}
                    <div className="fixed inset-0 z-10" {...overlayDismiss(() => setOpen(false))} />
                    <div className="absolute right-0 top-9 z-20 w-64 rounded-lg border border-cream-200 bg-white shadow-lg p-3">
                        <div className="text-[11px] text-ink-500 mb-2">{t('report.pickMonth')}</div>
                        <div className="flex justify-center mb-3">
                            <MonthPager month={month} onChange={setMonth} />
                        </div>
                        <p className="text-[11px] text-ink-400 leading-relaxed mb-3">{t('report.hint')}</p>
                        <button
                            onClick={go}
                            disabled={downloading}
                            className="w-full h-8 rounded-md bg-accent-500 text-white text-[12px] font-medium hover:bg-accent-600 transition disabled:opacity-50 flex items-center justify-center gap-1.5"
                        >
                            {downloading
                                ? <RefreshCw size={13} className="animate-spin" />
                                : <Download size={13} />}
                            {t('report.download')}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}

function RangeSwitcher({ rangeKey, onChange, customFrom, setCustomFrom, customTo, setCustomTo, invalid, days, onSearch, busy = false }) {
    const { t } = useTranslation('dashboard');
    return (
        <div className="flex flex-col items-end gap-2" aria-busy={busy}>
            <div className="inline-flex items-center gap-0 rounded-full border border-cream-200 bg-white p-0.5">
                {RANGES.map((r) => {
                    const active = r.key === rangeKey;
                    return (
                        <button
                            key={r.key}
                            onClick={() => onChange(r.key)}
                            disabled={busy}
                            className={[
                                'px-3.5 py-1 rounded-full text-[12px] font-medium tracking-tight transition',
                                'disabled:cursor-not-allowed',
                                active
                                    ? 'bg-accent-500 text-white shadow-[0_1px_0_rgba(0,0,0,0.04)]'
                                    : 'text-ink-500 hover:text-ink-900 disabled:hover:text-ink-500',
                                busy && !active ? 'opacity-45' : '',
                            ].join(' ')}
                        >
                            {t(r.labelKey)}
                        </button>
                    );
                })}
            </div>
            {/* 日期起訖：常駐一列。互動照 CallsPage：min/max 互鎖、沒填齊就不套用 */}
            <div className="flex items-center justify-end gap-1.5">
                <input
                    type="date"
                    value={customFrom}
                    max={customTo || undefined}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    disabled={busy}
                    className="px-2 py-1 text-xs border border-cream-300 rounded-md bg-white text-ink-700 focus:outline-none focus:border-accent-400 disabled:opacity-45 disabled:cursor-not-allowed"
                />
                <span className="text-xs text-ink-400">{t('range.to')}</span>
                <input
                    type="date"
                    value={customTo}
                    min={customFrom || undefined}
                    onChange={(e) => setCustomTo(e.target.value)}
                    disabled={busy}
                    className="px-2 py-1 text-xs border border-cream-300 rounded-md bg-white text-ink-700 focus:outline-none focus:border-accent-400 disabled:opacity-45 disabled:cursor-not-allowed"
                />
                <button
                    onClick={onSearch}
                    title={t('range.search')}
                    disabled={busy}
                    className="size-7 rounded-md border border-cream-300 bg-white hover:bg-cream-100 transition flex items-center justify-center text-ink-500 disabled:opacity-45 disabled:cursor-not-allowed disabled:hover:bg-white"
                >
                    <Search size={13} />
                </button>
            </div>
            {invalid && (
                <div className="text-[11.5px] text-danger-500 text-right">
                    {invalid === 'too_long'
                        ? t('range.tooLong', { days, max: MAX_CUSTOM_DAYS })
                        : t('range.reversed')}
                </div>
            )}
        </div>
    );
}

/* ───────────────────────── KPI strip ───────────────────────── */
/**
 * 通話組成圓餅。用 stroke-dasharray 畫環，不引外部圖表庫。
 * 只畫 n>0 的類別 —— 0% 的細線在小尺寸下只會變成雜訊。
 */
/**
 * 通話組成：100% 堆疊橫條。
 *
 * 為什麼不是甜甜圈：實際資料常是「單一類別佔 98%」（honghwa 外撥 9,734 / 9,979），
 * 圓周上另外三類只剩髮絲，得靠人為加最小弧長才看得見 —— 那已經是在美化而非呈現比例。
 * 橫條把同樣的比例攤在一條線上，1% 仍是可辨識的刻度；而且它不跟清單搶水平空間，
 * 清單因此拿得到整張卡的寬度（中文標籤不再被截斷）。
 * 無障礙：色塊只是輔助，每一列都有圖示＋文字＋精確值，不依賴顏色分辨。
 */
function CallsBar({ slices, total, label }) {
    const MIN = 1.2;   // 佔比 <1.2% 仍給最小可見寬度，精確值一律看下方清單
    const raw = slices.map((x) => (total > 0 ? (x.n / total) * 100 : 0));
    const lifted = raw.map((v) => (v > 0 && v < MIN ? MIN : v));
    const sum = lifted.reduce((a, b) => a + b, 0) || 1;
    return (
        <div
            className="h-2.5 w-full rounded-full overflow-hidden bg-cream-100 flex gap-px"
            role="img" aria-label={label}
        >
            {total > 0 && slices.map(({ key, color }, i) => (
                lifted[i] > 0 ? (
                    <div key={key} style={{ width: `${(lifted[i] / sum) * 100}%`, background: color }} />
                ) : null
            ))}
        </div>
    );
}

function KpiStrip({ loading, data, rangeLabel }) {
    const { t } = useTranslation('dashboard');
    const totals = data?.totals;
    const dur = formatDuration(totals?.durationSec || 0);
    // 🔴 0915：原本 outboundPct = 100 − inboundPct，但 web 通話兩者都不是（沒有 direction）
    //    → 宏華 CTI02 實案：OUT 顯示 0 通卻畫 58%、26 通卻畫 40%。改成各自算自己的比例。
    // 📌 0915：通話組成五類 —— 電話（IN/OUT）與網頁（語音/虛擬人/文字）。
    //    網頁三類都沒有 direction，只列 IN/OUT 會讓 178 + 26 ≠ 236（宏華 CTI02 實案）。
    //    五段相加 = TOTAL CALLS，圓餅才有意義。
    const calls = totals?.calls || 0;
    const pct = (n) => (calls > 0 ? ((n || 0) / calls) * 100 : 0);
    // 37 通 / 9974 = 0.37% → 四捨五入成 "0%" 看起來像壞掉（實際有資料）。
    // 佔比極小但非零時顯示 <1%，0 通才是 0%。
    const pctLabel = (n) => {
        const v = pct(n);
        if (v === 0) return '0%';
        if (v < 1) return '<1%';
        return `${Math.round(v)}%`;
    };
    // 五類的顏色刻意「色相」拉開，不靠明度分 —— 舊配色裡外撥 #c84a2c 與虛擬人 #a83a1f
    // 是同一支紅棕只差亮度、網頁 ink-400 與文字 ink-300 是同一階灰，橫條上相鄰時分不出來（0918 回報）。
    // 現在：綠 98° → 紅 12° → 藍 208° → 紫 274° → 青 180°，任兩色色相差 ≥ 68°；藍/紫/青取自本頁長條圖 CHART_COLORS 同一套色盤。
    const slices = [
        { key: 'inbound', n: totals?.inboundCalls || 0, color: 'var(--color-success-500)', Icon: PhoneIncoming, group: 'phone' },
        { key: 'outbound', n: totals?.outboundCalls || 0, color: 'var(--color-accent-500)', Icon: PhoneOutgoing, group: 'phone' },
        { key: 'web', n: totals?.webCalls || 0, color: '#5b7a96', Icon: Globe, group: 'web' },
        { key: 'webAvatar', n: totals?.webAvatarCalls || 0, color: '#8a6aa3', Icon: UserSquare2, group: 'web' },
        { key: 'webText', n: totals?.webTextCalls || 0, color: '#3d8f8a', Icon: MessageSquare, group: 'web' },
    ];
    const shown = slices.filter((x) => x.n > 0);
    const phoneN = slices.filter((x) => x.group === 'phone').reduce((a, x) => a + x.n, 0);
    const webN = slices.filter((x) => x.group === 'web').reduce((a, x) => a + x.n, 0);
    const hasKpiSplit = !!totals && (phoneN > 0 || webN > 0);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <Card className="lg:col-span-5 reveal" padded={false}>
                <div className="p-7 relative overflow-hidden">
                    <div className="absolute inset-0 pointer-events-none opacity-[0.07]">
                        <DotPatternBg />
                    </div>
                    <div className="relative">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-400 mb-3 flex items-baseline justify-between gap-3">
                            <span>total airtime</span>
                            {/* 📌 0915：AIRTIME 跟著上方標籤變，計費卡卻固定本月 —— 兩個區間並列才不會被誤認為同一段 */}
                            {rangeLabel && <span className="tracking-[0.15em] text-ink-300 normal-case">{rangeLabel}</span>}
                        </div>
                        <div className="heading-display tnum text-ink-900 leading-none flex items-baseline gap-2 flex-wrap">
                            {loading && !data ? (
                                <span className="text-5xl text-ink-300">—</span>
                            ) : (
                                <>
                                    <span className="text-[64px] font-semibold tracking-[-0.025em]">{dur.h}</span>
                                    <span className="text-[14px] font-mono text-ink-400 uppercase tracking-[0.2em] mr-3">hr</span>
                                    <span className="text-[64px] font-semibold tracking-[-0.025em]">{String(dur.m).padStart(2, '0')}</span>
                                    <span className="text-[14px] font-mono text-ink-400 uppercase tracking-[0.2em] mr-3">min</span>
                                    <span className="text-[28px] font-medium text-ink-500">{String(dur.s).padStart(2, '0')}</span>
                                    <span className="text-[11px] font-mono text-ink-400 uppercase tracking-[0.2em]">sec</span>
                                </>
                            )}
                        </div>
                        <p className="mt-4 text-sm text-ink-500 max-w-md leading-relaxed">
                            {t('kpi.airtime.desc')}
                        </p>
                        {/* 📌 0915：AIRTIME 是 phone + web 的總和，但 IN/OUT 只算 phone（web 沒有 direction）
                            → 這裡把組成列出來，格式比照計費卡的分項，數字才對得起來。 */}
                        {/* 一行一項 —— flex-wrap 會依寬度亂折（三項時變成 2+1，看起來像沒對齊） */}
                        {hasKpiSplit && (
                            <div className="flex flex-col gap-1.5 text-[10px] font-mono uppercase tracking-[0.15em] text-ink-500 mt-2.5">
                                {/* 只列有資料的。電話不再拆進線/外撥 —— 拆完最多五行，
                                    這張卡塞不下也沒必要（進線/外撥的比例右邊 TOTAL CALLS 就有）。 */}
                                {[
                                    ['splitPhone', totals?.phoneDurationSec, totals?.phoneCalls, 'bg-success-500/85'],
                                    ['splitWeb', totals?.webDurationSec, totals?.webCalls, '[background:#5b7a96]'],
                                    ['splitAvatar', totals?.webAvatarDurationSec, totals?.webAvatarCalls, '[background:#8a6aa3]'],
                                    ['splitText', totals?.webTextDurationSec, totals?.webTextCalls, '[background:#3d8f8a]'],
                                ].filter(([, , n]) => (n || 0) > 0).map(([key, sec, n, dot]) => (
                                    <span key={key} className="inline-flex items-center gap-1.5">
                                        <span className={`size-1.5 rounded-full ${dot}`} />
                                        {t(`kpi.airtime.${key}`, { value: preciseDuration(sec || 0), n: n || 0 })}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </Card>

            {/* 📌 0915：TOTAL CALLS 與 IN/OUT 合併 —— 總數與組成本來就該一起看，
                拆兩張卡會讓「五類相加 = 總數」這件事看不出來。 */}
            <Card className="lg:col-span-4 reveal" style={{ animationDelay: '80ms' }} padded={false}>
                <div className="p-6">
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-400 mb-4">
                        {t('kpi.totalCalls.label')}
                    </div>
                    {loading && !data ? (
                        <span className="heading-display text-3xl text-ink-300">—</span>
                    ) : (
                        <div>
                            <div className="flex items-baseline justify-between gap-3 mb-2.5">
                                <span className="heading-display tnum text-[32px] font-semibold leading-none text-ink-900">
                                    {calls.toLocaleString()}
                                </span>
                                <span className="text-xs text-ink-400">{t('kpi.totalCalls.sub')}</span>
                            </div>

                            {/* 一條總橫條：所有類別攤在同一條，比例以 TOTAL CALLS 為分母 */}
                            <CallsBar
                                slices={shown} total={calls}
                                label={`${t('kpi.totalCalls.label')}：${calls.toLocaleString()} — ${shown.map((x) => `${t(`kpi.slice.${x.key}`)} ${x.n.toLocaleString()} ${pctLabel(x.n)}`).join('、')}`}
                            />

                            {/* 清單是橫條的無障礙 fallback（色塊只靠顏色分類，色盲讀不出來）；
                                0 通的類別與群組一律不列 */}
                            <div className="mt-4 space-y-3">
                                {[['phone', phoneN], ['web', webN]].filter(([, n]) => n > 0).map(([group, groupN]) => (
                                    <div key={group}>
                                        <div className="flex items-baseline gap-2 pb-1 border-b border-cream-200">
                                            <span className="shrink-0 text-xs font-medium text-ink-700">{t(`kpi.group.${group}`)}</span>
                                            <span className="font-mono text-[11px] tnum text-ink-400 ml-auto">{groupN.toLocaleString()}</span>
                                        </div>
                                        <div className="mt-1 space-y-1">
                                            {slices.filter((x) => x.group === group && x.n > 0).map(({ key, n, color, Icon }) => (
                                                <div key={key} className="flex items-center gap-2 text-xs">
                                                    <Icon size={13} strokeWidth={2} style={{ color }} className="shrink-0" />
                                                    {/* 標籤只有兩個字（進線／外撥／語音／文字）—— 不可 truncate */}
                                                    <span className="shrink-0 text-ink-600">{t(`kpi.slice.${key}`)}</span>
                                                    <span className="ml-auto font-mono tnum text-[13px] font-medium text-ink-900">{n.toLocaleString()}</span>
                                                    <span className="shrink-0 font-mono tnum text-[11px] text-ink-400 w-10 text-right">{pctLabel(n)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </Card>

            <Card className="lg:col-span-3 reveal" style={{ animationDelay: '160ms' }}>
                <KpiCell label="avg duration" valueNode={
                    <span className="heading-display tnum text-[32px] font-semibold leading-none">
                        {loading && !data ? '—' : shortDuration(totals?.avgDurationSec || 0)}
                    </span>
                } subLabel={t('kpi.avgDuration.sub')} />
            </Card>

        </div>
    );
}

function KpiCell({ label, valueNode, subLabel }) {
    return (
        <div className="py-1">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-400 mb-3">
                {label}
            </div>
            <div className="text-ink-900">{valueNode}</div>
            {subLabel && <div className="mt-2 text-xs text-ink-500">{subLabel}</div>}
        </div>
    );
}

/* ───────────────────────── Billing panel ─────────────────────────
   本月用量 vs 配額；超額部分以分鐘 × 單價計費（分鐘無條件進位）
   ───────────────────────────────────────────────────────────────────── */
function BillingPanel({ monthData, monthRange, config }) {
    const { t } = useTranslation('dashboard');
    // 網頁通話（webcall）按 webMinuteFactor（預設 0.5）折算計入額度；電話 1:1。usedSec = 折算後秒數。
    // ⚠️ 用 *BillableSec（已扣「轉真人 ring 等待」）而非 *DurationSec —— 月帳單 saas-admin/billing/overview
    //    就是拿 billableSec 算的，這裡跟著同一口徑，否則客戶在後台看到的分鐘會比實際帳單高。
    //    舊 log 沒有 billableSec 時 aggregateStats 已自動退回 durationSec，這裡再退一層防舊 payload。
    const mTotals = monthData?.totals || {};
    const webFactor = config.webMinuteFactor ?? 0.5;
    const webSec = mTotals.webBillableSec ?? mTotals.webDurationSec ?? 0;
    // 虛擬人（web_avatar）全額計費，不打網頁折 —— 與後端 saasAdmin.billableSecOf 同口徑
    const avatarSec = mTotals.webAvatarBillableSec ?? mTotals.webAvatarDurationSec ?? 0;
    const phoneSec = mTotals.phoneBillableSec
        ?? mTotals.phoneDurationSec
        ?? Math.max(0, (mTotals.billableSec ?? mTotals.durationSec ?? 0) - webSec);
    const usedSec = phoneSec + avatarSec + webSec * webFactor;
    const hasSplit = phoneSec > 0 || webSec > 0 || avatarSec > 0;
    const isUnlimited = config.monthlyQuotaMinutes == null;
    const quotaSec = isUnlimited ? 0 : (config.monthlyQuotaMinutes || 0) * 60;
    const overageSec = isUnlimited ? 0 : Math.max(0, usedSec - quotaSec);
    const overageMinutes = overageSec > 0 ? Math.ceil(overageSec / 60) : 0;
    const overageCost = overageMinutes * (config.overageRateUsdPerMin || 0);
    const pct = quotaSec > 0 ? Math.min(100, (usedSec / quotaSec) * 100) : 0;
    const phonePct = quotaSec > 0 ? Math.min(100, (phoneSec / quotaSec) * 100) : 0;
    const webPct = Math.max(0, pct - phonePct);
    const overPct = quotaSec > 0 && usedSec > quotaSec ? Math.min(100, ((usedSec - quotaSec) / quotaSec) * 100) : 0;
    const isOver = overageSec > 0;
    const isNear = !isOver && pct >= 80;

    // ⚠️ ceil 不是 floor —— 帳單（saas-admin/billing/overview 的 minutesUsed）就是無條件進位，
    //    floor 會讓客戶後台永遠比帳單少 1 分鐘（實案：CTI02 後台 469、帳單 470）。
    const usedTotalMin = Math.ceil(usedSec / 60);

    return (
        <Card padded={false} className="mb-10 overflow-hidden">
            <div className="p-7 relative">
                <div className="absolute inset-0 pointer-events-none opacity-[0.04]">
                    <DotPatternBg />
                </div>
                <div className="relative">
                    <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
                        <div className="inline-flex items-center gap-2">
                            <span className="size-1.5 rounded-full bg-accent-500" />
                            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">
                                billing · {monthRange.label}
                            </span>
                        </div>
                        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-ink-400">
                            {/* 「本月進度」在自訂區間下不成立（沒有「第幾天 / 共幾天」可言）→ 改顯示區間天數 */}
                            {monthRange.custom
                                ? `${monthRange.days}d range`
                                : `day ${monthRange.dayOfMonth} / ${monthRange.daysInMonth}・remaining ${monthRange.remainingDays}d`}
                        </div>
                    </div>
                    <div className="flex items-baseline gap-4 mb-6 flex-wrap">
                        <h2 className="heading-display text-2xl font-semibold text-ink-900 tracking-tight">
                            {monthRange.custom ? t('billing.titleRange') : t('billing.title')}
                        </h2>
                        {config.planName && (
                            <span className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full bg-accent-50 text-accent-700 text-[11px] font-medium">
                                {config.planName}
                                <span className="font-mono text-[9px] opacity-60 uppercase tracking-[0.18em]">{config.planId}</span>
                            </span>
                        )}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                        {/* Usage progress */}
                        <div className="lg:col-span-7">
                            <div className="flex items-baseline justify-between mb-3">
                                <div className="tnum">
                                    <span className="heading-display text-[40px] font-semibold text-ink-900 leading-none">
                                        {usedTotalMin.toLocaleString()}
                                    </span>
                                    <span className="font-mono text-[11px] text-ink-400 ml-1.5 uppercase tracking-[0.18em]">min</span>
                                    <span className="text-ink-300 mx-2.5">/</span>
                                    {isUnlimited ? (
                                        <span className="text-2xl font-medium text-ink-500">∞</span>
                                    ) : (
                                        <span className="text-2xl font-medium text-ink-500">{config.monthlyQuotaMinutes.toLocaleString()}</span>
                                    )}
                                    <span className="font-mono text-[10px] text-ink-400 ml-1.5 uppercase tracking-[0.18em]">
                                        {isUnlimited ? 'unlimited' : 'quota'}
                                    </span>
                                    {/* 含量怎麼來的：5 席 × 800 分鐘 = 4,000。不寫清楚使用者
                                        會以為 quota 是固定值，看到跟別的租戶不一樣就來問。 */}
                                    {/* 吃到飽（monthlyQuotaMinutes=null）不顯示 —— 否則會印出
                                        「3 席 × 每席 null 分鐘」（實跑多席情境時抓到）。 */}
                                    {!isUnlimited && config.seatCount > 1 && config.quotaMinutesPerSeat > 0 && (
                                        <div className="font-mono text-[10px] text-ink-400 mt-1.5 tracking-[0.1em] normal-case">
                                            {t('billing.quotaFromSeats', {
                                                seats: config.seatCount,
                                                perSeat: (config.quotaMinutesPerSeat || 0).toLocaleString(),
                                            })}
                                        </div>
                                    )}
                                </div>
                                <span className={[
                                    'text-[11px] font-mono uppercase tracking-[0.18em]',
                                    isUnlimited ? 'text-accent-500' : isOver ? 'text-accent-700' : isNear ? 'text-warn-500' : 'text-success-500',
                                ].join(' ')}>
                                    {isUnlimited ? 'unlimited' : isOver ? 'over' : isNear ? 'near limit' : 'within plan'}
                                </span>
                            </div>

                            {isUnlimited ? (
                                /* Unlimited — 不畫進度條，改用 striped pattern 帶 "∞" 提示 */
                                <div className="relative h-3 rounded-full overflow-hidden bg-cream-100">
                                    <div className="absolute inset-0 bg-gradient-to-r from-accent-500/0 via-accent-500/25 to-accent-500/0 animate-pulse" />
                                </div>
                            ) : (
                                <>
                                    <div className="relative h-3 rounded-full bg-cream-100 overflow-hidden">
                                        {/* 電話段（實色）+ 網頁折算段（同色淡化）疊成總用量 */}
                                        <div
                                            className={[
                                                'absolute inset-y-0 left-0 rounded-l-full transition-all',
                                                isOver ? 'bg-ink-700/85' : isNear ? 'bg-warn-500/85' : 'bg-success-500/85',
                                            ].join(' ')}
                                            style={{ width: `${phonePct}%` }}
                                        />
                                        <div
                                            className={[
                                                'absolute inset-y-0 rounded-r-full transition-all',
                                                isOver ? 'bg-ink-700/40' : isNear ? 'bg-warn-500/40' : 'bg-success-500/40',
                                            ].join(' ')}
                                            style={{ left: `${phonePct}%`, width: `${webPct}%` }}
                                        />
                                        {isOver && (
                                            <div
                                                className="absolute inset-y-0 rounded-r-full bg-accent-500/85 transition-all"
                                                style={{ left: '100%', width: `${overPct}%` }}
                                            />
                                        )}
                                        <div
                                            className="absolute inset-y-0 w-px bg-ink-900/20"
                                            style={{ left: '100%' }}
                                        />
                                    </div>
                                </>
                            )}

                            <div className="flex justify-between text-[10px] font-mono uppercase tracking-[0.15em] text-ink-400 mt-2">
                                <span>{t('billing.used', { value: preciseDuration(usedSec) })}</span>
                                {isUnlimited ? (
                                    <span>{t('billing.unlimitedNoMinutes')}</span>
                                ) : (
                                    <span>{t('billing.remaining', { value: preciseDuration(Math.max(0, quotaSec - usedSec)) })}</span>
                                )}
                            </div>
                            {hasSplit && (
                                <div className="flex items-center gap-4 text-[10px] font-mono uppercase tracking-[0.15em] text-ink-500 mt-1.5">
                                    <span className="inline-flex items-center gap-1.5">
                                        <span className={['size-1.5 rounded-full', isOver ? 'bg-ink-700/85' : isNear ? 'bg-warn-500/85' : 'bg-success-500/85'].join(' ')} />
                                        {t('billing.splitPhone', { value: preciseDuration(phoneSec) })}
                                    </span>
                                    <span className="inline-flex items-center gap-1.5">
                                        <span className={['size-1.5 rounded-full', isOver ? 'bg-ink-700/40' : isNear ? 'bg-warn-500/40' : 'bg-success-500/40'].join(' ')} />
                                        {t('billing.splitWeb', { value: preciseDuration(webSec), factor: webFactor })}
                                    </span>
                                    {avatarSec > 0 && (
                                        <span className="inline-flex items-center gap-1.5">
                                            <span className={['size-1.5 rounded-full', isOver ? 'bg-ink-700/60' : isNear ? 'bg-warn-500/60' : 'bg-accent-700/70'].join(' ')} />
                                            {t('billing.splitAvatar', { value: preciseDuration(avatarSec) })}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Overage cost */}
                        <div className="lg:col-span-5 lg:border-l lg:border-cream-200 lg:pl-8">
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-400 mb-3">
                                {isUnlimited ? 'this plan' : 'overage this month'}
                            </div>
                            {isUnlimited ? (
                                <>
                                    <div className="flex items-baseline gap-2 mb-2">
                                        <span className="heading-display tnum font-semibold leading-none tracking-tight text-accent-700 text-[44px]">
                                            {t('billing.noLimit')}
                                        </span>
                                    </div>
                                    <div className="text-xs text-ink-500 leading-relaxed">
                                        {t('billing.unlimitedDesc')}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="flex items-baseline gap-2 mb-2">
                                        <span className={[
                                            'heading-display tnum font-semibold leading-none tracking-tight',
                                            isOver ? 'text-accent-700 text-[44px]' : 'text-ink-300 text-[36px]',
                                        ].join(' ')}>
                                            ${overageCost.toFixed(2)}
                                        </span>
                                        <span className="font-mono text-[10px] text-ink-400 uppercase tracking-[0.2em]">usd</span>
                                    </div>
                                    <div className="text-xs text-ink-500 leading-relaxed">
                                        {isOver ? (
                                            <>
                                                {t('billing.overagePrefix')} <span className="font-medium text-ink-900 tnum">{overageMinutes.toLocaleString()}</span> {t('billing.overageSuffix')}
                                                <span className="text-ink-400"> @ </span>
                                                <span className="tnum">${config.overageRateUsdPerMin.toFixed(2)}</span>
                                                <span className="text-ink-400"> / min</span>
                                            </>
                                        ) : (
                                            <>{t('billing.noOveragePrefix')} <span className="tnum">${config.overageRateUsdPerMin.toFixed(2)}</span> {t('billing.noOverageSuffix')}</>
                                        )}
                                        {config.loaded === false && (
                                            <span className="block mt-1.5 text-[10px] text-ink-400 italic">
                                                {t('billing.planLoading')}
                                            </span>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Card>
    );
}

/* ───────────────────────── Termination breakdown ───────────────────────── */
const TERM_META = {
    user_hangup: { labelKey: 'term.user_hangup.label', subKey: 'term.user_hangup.sub', icon: PhoneOff, tone: 'ink', color: 'var(--color-ink-700)' },
    end_call: { labelKey: 'term.end_call.label', subKey: 'term.end_call.sub', icon: CheckCircle2, tone: 'success', color: 'var(--color-success-500)' },
    transfer: { labelKey: 'term.transfer.label', subKey: 'term.transfer.sub', icon: PhoneForwarded, tone: 'accent', color: 'var(--color-accent-500)' },
    unexpected: { labelKey: 'term.unexpected.label', subKey: 'term.unexpected.sub', icon: CircleAlert, tone: 'danger', color: 'var(--color-danger-500)' },
    other: { labelKey: 'term.other.label', subKey: 'term.other.sub', icon: CircleAlert, tone: 'ink', color: 'var(--color-ink-400)' },
};

function TerminationBreakdown({ byTermination, totalCalls }) {
    const { t } = useTranslation('dashboard');
    if (!byTermination || !totalCalls) {
        return (
            <Card padded={false}>
                <Empty icon={PhoneOff} title={t('common:state.empty')} />
            </Card>
        );
    }
    const items = Object.entries(byTermination)
        .filter(([, n]) => n > 0)
        .map(([key, n]) => ({
            key,
            count: n,
            pct: Math.round((n / totalCalls) * 100),
            meta: TERM_META[key] || TERM_META.other,
        }))
        .sort((a, b) => b.count - a.count);

    return (
        <Card>
            <div className="space-y-3.5">
                {items.map((it) => {
                    const Icon = it.meta.icon;
                    return (
                        <div key={it.key} className="flex items-center gap-4">
                            <div className="flex items-center gap-2.5 w-36 shrink-0">
                                <Icon size={14} strokeWidth={1.8} style={{ color: it.meta.color }} />
                                <div>
                                    <div className="text-sm font-medium text-ink-900">{t(it.meta.labelKey)}</div>
                                    <div className="text-[10px] text-ink-400">{t(it.meta.subKey)}</div>
                                </div>
                            </div>
                            <div className="flex-1 relative h-2 rounded-full bg-cream-100 overflow-hidden">
                                <div
                                    className="absolute inset-y-0 left-0 rounded-full transition-all"
                                    style={{ width: `${it.pct}%`, backgroundColor: it.meta.color, opacity: 0.75 }}
                                />
                            </div>
                            <div className="w-24 text-right tnum">
                                <span className="text-base font-semibold text-ink-900">{it.count}</span>
                                <span className="text-xs text-ink-400 ml-1.5">{t('unit.calls')}</span>
                            </div>
                            <div className="w-12 text-right tnum font-mono text-xs text-ink-500">
                                {it.pct}%
                            </div>
                        </div>
                    );
                })}
            </div>
        </Card>
    );
}

/* ───────────────────────── Daily trend chart ───────────────────────── */
function DailyTrendChart({ byDay, fromMillis, toMillis }) {
    const { t } = useTranslation('dashboard');
    const series = useMemo(() => buildSeries(byDay, fromMillis, toMillis), [byDay, fromMillis, toMillis]);
    const [hover, setHover] = useState(null);   // 滑鼠所在那天的 index
    const W = 1000;
    const H = 240;
    const PAD = { t: 16, r: 24, b: 36, l: 44 };
    const cw = W - PAD.l - PAD.r;
    const ch = H - PAD.t - PAD.b;

    const maxMin = Math.max(1, ...series.map((d) => d.minutes));
    const maxCnt = Math.max(1, ...series.map((d) => d.calls));
    const xStep = series.length > 1 ? cw / (series.length - 1) : cw;
    const x = (i) => PAD.l + i * xStep;
    const yMin = (v) => PAD.t + ch - (v / maxMin) * ch;
    const yCnt = (v) => PAD.t + ch - (v / maxCnt) * ch;

    const linePath = series.map((d, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${yMin(d.minutes)}`).join(' ');
    const areaPath = `${linePath} L ${x(series.length - 1)} ${PAD.t + ch} L ${x(0)} ${PAD.t + ch} Z`;
    const callsPath = series.map((d, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${yCnt(d.calls)}`).join(' ');

    const empty = series.every((d) => d.calls === 0);

    return (
        <div className="relative">
            <div className="absolute top-1 right-1 flex items-center gap-4 text-[10px] font-mono uppercase tracking-[0.2em] text-ink-400">
                <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block w-3 h-[3px] rounded-full bg-accent-500" /> {t('legend.callMinutes')}
                </span>
                <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block w-3 h-[3px] rounded-full border-t border-dashed border-ink-400" /> {t('legend.callCount')}
                </span>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[240px]" preserveAspectRatio="none">
                <defs>
                    <linearGradient id="dashboard-area" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor="var(--color-accent-500)" stopOpacity="0.22" />
                        <stop offset="100%" stopColor="var(--color-accent-500)" stopOpacity="0.02" />
                    </linearGradient>
                </defs>

                {[0.25, 0.5, 0.75, 1].map((t) => (
                    <line
                        key={t}
                        x1={PAD.l}
                        x2={W - PAD.r}
                        y1={PAD.t + ch - ch * t}
                        y2={PAD.t + ch - ch * t}
                        stroke="var(--color-cream-200)"
                        strokeWidth="1"
                        strokeDasharray="2 4"
                    />
                ))}

                {!empty && (
                    <>
                        <path d={areaPath} fill="url(#dashboard-area)" />
                        <path d={linePath} fill="none" stroke="var(--color-accent-500)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                        <path d={callsPath} fill="none" stroke="var(--color-ink-400)" strokeWidth="1.25" strokeDasharray="3 3" strokeLinejoin="round" />

                        {hover != null && <line x1={x(hover)} x2={x(hover)} y1={PAD.t} y2={PAD.t + ch} stroke="var(--color-ink-300)" strokeWidth="1" strokeDasharray="2 3" />}
                        {series.map((d, i) => (
                            <g key={d.date} className="dashboard-dot">
                                <circle
                                    cx={x(i)}
                                    cy={yMin(d.minutes)}
                                    r={3}
                                    fill="var(--color-cream-50)"
                                    stroke="var(--color-accent-500)"
                                    strokeWidth="1.5"
                                />
                            </g>
                        ))}
                        {/* 每天一條看不見的直欄當滑鼠感應區（整欄高度都算，不用對準小圓點） */}
                        {series.map((d, i) => (
                            <rect key={`h-${d.date}`} x={x(i) - xStep / 2} y={PAD.t} width={Math.max(xStep, 8)} height={ch} fill="transparent"
                                onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover((h) => (h === i ? null : h))} />
                        ))}
                    </>
                )}

                {[0, 0.5, 1].map((t) => (
                    <text
                        key={`yL-${t}`}
                        x={PAD.l - 8}
                        y={PAD.t + ch - ch * t + 4}
                        textAnchor="end"
                        className="fill-[var(--color-ink-400)]"
                        style={{ fontSize: 10, fontFamily: 'var(--font-mono)', letterSpacing: '0.05em' }}
                    >
                        {Math.round(maxMin * t)}m
                    </text>
                ))}

                {series.map((d, i) => {
                    // X 軸標籤疏密看「實際畫了幾天」（series.length）——
                    // 0915 之前是看 days prop，改吃起訖後那個 prop 已移除（ReferenceError: days is not defined）。
                    const n = series.length;
                    const stride = n <= 7 ? 1 : n <= 14 ? 2 : 4;
                    if (i % stride !== 0 && i !== series.length - 1) return null;
                    const [, mm, dd] = d.date.split('-');
                    return (
                        <text
                            key={`x-${d.date}`}
                            x={x(i)}
                            y={H - 12}
                            textAnchor="middle"
                            className="fill-[var(--color-ink-400)]"
                            style={{ fontSize: 10, fontFamily: 'var(--font-mono)', letterSpacing: '0.05em' }}
                        >
                            {`${mm}/${dd}`}
                        </text>
                    );
                })}
            </svg>

            {!empty && hover != null && series[hover] && (() => {
                const d = series[hover];
                const leftPct = (x(hover) / W) * 100;
                return (
                    <div className="pointer-events-none absolute top-6 z-10 min-w-[170px] rounded-md border border-cream-200 bg-white px-3 py-2 text-xs shadow-md"
                        style={leftPct > 70 ? { right: `${100 - leftPct}%`, marginRight: 10 } : { left: `${leftPct}%`, marginLeft: 10 }}>
                        <div className="font-mono text-ink-900 mb-1">{d.date}</div>
                        <div className="text-ink-900 tnum">{t('chart.tt.total', { calls: d.calls, minutes: Math.round(d.minutes) })}</div>
                        <div className="mt-1 text-ink-500 tnum space-y-0.5">
                            <div>{t('chart.tt.inbound', { n: d.inbound })}</div>
                            <div>{t('chart.tt.outbound', { n: d.outbound })}</div>
                            <div>{t('chart.tt.web', { n: d.web })}</div>
                            <div>{t('chart.tt.answered', { n: d.answered })}</div>
                        </div>
                    </div>
                );
            })()}

            {empty && (
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center text-ink-400">
                        <TrendingUp size={32} strokeWidth={1} className="mx-auto mb-2 opacity-60" />
                        <p className="text-sm">{t('chart.empty')}</p>
                    </div>
                </div>
            )}
        </div>
    );
}

/**
 * byDay → 補滿每一天的序列。
 *
 * 🔴 0915 修正：原本簽名是 (byDay, days)，從「今天」往回推 days 天。preset 剛好對得上，
 *    但**自訂區間整個畫錯** —— 選 8/01–8/31（days=31）會畫成 8/16–9/15：
 *    8/01–8/15 的資料查到了畫不出來，9/01–9/15 多出 15 根空柱子。
 *    改吃 fromMillis/toMillis，圖與上方標籤、與後端查詢範圍三者一致。
 */
function buildSeries(byDay, fromMillis, toMillis) {
    const tzOffset = 8 * 3600 * 1000;
    const dayStart = (ms) => Math.floor((ms + tzOffset) / 86400000) * 86400000 - tzOffset;
    const map = new Map((byDay || []).map((d) => [d.date, d]));
    const out = [];
    // 起訖顛倒、或沒帶參數（NaN）→ 下面的迴圈條件自然不成立，回空陣列。不另外加防呆，
    // 那兩行是冗餘的（突變驗證：拿掉照樣綠）。
    const first = dayStart(fromMillis);
    const last = dayStart(toMillis);
    for (let ms = first; ms <= last; ms += 86400000) {
        const d8 = new Date(ms + tzOffset);
        const date = `${d8.getUTCFullYear()}-${String(d8.getUTCMonth() + 1).padStart(2, '0')}-${String(d8.getUTCDate()).padStart(2, '0')}`;
        const hit = map.get(date);
        out.push({
            date,
            calls: hit?.calls || 0,
            minutes: (hit?.durationSec || 0) / 60,
            inbound: hit?.inboundCalls || 0,
            outbound: hit?.outboundCalls || 0,
            web: hit?.webVoiceCalls || 0,
            answered: hit?.answeredCalls || 0,
        });
    }
    return out;
}

/* ───────────────────────── Avatar ranking table ───────────────────────── */
function AvatarRanking({ rows, totalDurationSec, loading, onPick }) {
    const { t } = useTranslation('dashboard');
    if (!rows || rows.length === 0) {
        return (
            <Card padded={false}>
                <Empty
                    icon={BarChart3}
                    title={loading ? t('common:state.loading') : t('common:state.empty')}
                    description={loading ? null : t('ranking.empty.desc')}
                />
            </Card>
        );
    }
    const maxDur = Math.max(1, ...rows.map((r) => r.durationSec));

    return (
        <Card padded={false}>
            <div className="overflow-x-auto">
                <table className="w-full text-sm tnum">
                    <thead>
                        <tr className="border-b border-cream-200">
                            <th className="text-left font-mono text-[10px] uppercase tracking-[0.22em] text-ink-400 px-6 py-3.5">{t('ranking.col.avatar')}</th>
                            <th className="text-right font-mono text-[10px] uppercase tracking-[0.22em] text-ink-400 px-3 py-3.5">{t('ranking.col.inbound')}</th>
                            <th className="text-right font-mono text-[10px] uppercase tracking-[0.22em] text-ink-400 px-3 py-3.5">{t('ranking.col.outbound')}</th>
                            <th className="text-right font-mono text-[10px] uppercase tracking-[0.22em] text-ink-400 px-3 py-3.5">{t('ranking.col.calls')}</th>
                            <th className="text-left font-mono text-[10px] uppercase tracking-[0.22em] text-ink-400 px-6 py-3.5 w-[36%]">{t('ranking.col.duration')}</th>
                            <th className="text-right font-mono text-[10px] uppercase tracking-[0.22em] text-ink-400 px-6 py-3.5">{t('ranking.col.avg')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => {
                            const pct = (r.durationSec / maxDur) * 100;
                            const totalShare = totalDurationSec ? Math.round((r.durationSec / totalDurationSec) * 100) : 0;
                            return (
                                <tr
                                    key={r.avatarId}
                                    onClick={() => onPick && onPick(r.avatarId)}
                                    className="border-b last:border-0 border-cream-100 hover:bg-cream-50 transition cursor-pointer"
                                >
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <span className="font-mono text-[10px] text-ink-300 w-5 text-right">
                                                {String(i + 1).padStart(2, '0')}
                                            </span>
                                            <div className="min-w-0">
                                                <div className="font-medium text-ink-900 truncate">{r.avatarName}</div>
                                                <div className="font-mono text-[10px] text-ink-300 mt-0.5 truncate">
                                                    {r.avatarId.slice(0, 8)}…
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-3 py-4 text-right">
                                        {r.inboundCalls > 0 ? (
                                            <span className="text-success-500 font-medium">{r.inboundCalls}</span>
                                        ) : (
                                            <span className="text-ink-300">—</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-4 text-right">
                                        {r.outboundCalls > 0 ? (
                                            <span className="text-accent-700 font-medium">{r.outboundCalls}</span>
                                        ) : (
                                            <span className="text-ink-300">—</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-4 text-right text-ink-900 font-medium">{r.calls}</td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="relative flex-1 h-1.5 rounded-full bg-cream-100 overflow-hidden min-w-[60px]">
                                                <div
                                                    className="absolute inset-y-0 left-0 bg-accent-500/80 rounded-full"
                                                    style={{ width: `${pct}%` }}
                                                />
                                            </div>
                                            <div className="text-ink-900 font-medium whitespace-nowrap min-w-[64px] text-right">
                                                {shortDuration(r.durationSec)}
                                            </div>
                                            <div className="font-mono text-[10px] text-ink-400 w-9 text-right">{totalShare}%</div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-right text-ink-500">{shortDuration(r.avgDurationSec)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </Card>
    );
}

/* ───────────────────────── Decorative bg dots ───────────────────────── */
function DotPatternBg() {
    return (
        <svg width="100%" height="100%" preserveAspectRatio="none">
            <defs>
                <pattern id="dashboard-dots" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                    <circle cx="2" cy="2" r="0.9" fill="var(--color-ink-700)" />
                </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#dashboard-dots)" />
        </svg>
    );
}

function StageStyles() {
    return (
        <style>{`
            .dashboard-stage .reveal {
                animation: dashboardReveal 520ms cubic-bezier(0.22, 0.61, 0.36, 1) both;
            }
            @keyframes dashboardReveal {
                from { opacity: 0; transform: translateY(8px); }
                to   { opacity: 1; transform: translateY(0); }
            }
        `}</style>
    );
}
